import { httpService } from 'src/services/app/http.service'
import { log } from 'src/services/app/log.service'
import { TICKET_ENDPOINTS } from 'src/services/consts/api.const'
import {
  TICKET_CUSTOM_IDS,
  TICKET_CUSTOM_ID_CUSTOM,
  TICKET_CUSTOM_ID_NEW,
} from 'src/services/consts/ticket-creation.const'
import {
  refactorTicket,
  refactorUploadedFiles,
  omitFileUploadAnswers,
} from 'src/services/helpers/ticket.helper'
import { isObjectEmpty } from 'src/services/app/util.service'

export const ticketService = {
  getTicketById,
  createTicket,
  updateTicket,
  uploadFileToTicket,
  deleteFileFromTicket,
  getFileLink,
}

async function getTicketById(ticketId) {
  const data = await httpService.post(TICKET_ENDPOINTS.GET_TICKET_BY_ID, {
    ticket_id: ticketId,
  })

  // Validate that response data exists
  if (isObjectEmpty(data)) {
    throw new Error('No ticket data found')
  }

  const ticket = refactorTicket({
    ticket_info: { ticket_id: ticketId, ...data.ticket_info },
    user_messages: data.user_messages,
  })
  log.info('Fetching ticket details for:', ticket)
  return ticket
}

async function createTicket({
  companyId,
  productId,
  companyName,
  productName,
  companyWebsite,
  productWebsite,
}) {
  const payload = {
    product_id: productId,
    company_id: companyId,
  }

  if (TICKET_CUSTOM_IDS.includes(companyId) && companyName) {
    payload.company_nf_name = companyName
    if (companyId === TICKET_CUSTOM_ID_CUSTOM) {
      payload.company_id = TICKET_CUSTOM_ID_NEW
    }
    if (companyWebsite) {
      payload.company_nf_website = companyWebsite
    }
  }
  if (TICKET_CUSTOM_IDS.includes(productId) && productName) {
    payload.product_nf_name = productName
    if (productId === TICKET_CUSTOM_ID_CUSTOM) {
      payload.product_id = TICKET_CUSTOM_ID_NEW
    }
    if (productWebsite) {
      payload.product_nf_website = productWebsite
    }
  }

  const res = await httpService.post(TICKET_ENDPOINTS.CREATE_TICKET, payload)

  // Validate that response data exists
  if (!res.ticket) {
    throw new Error('Failed to create ticket')
  }

  const refactoredTicket = refactorTicket({
    ticket_info: { ...res.ticket },
  })

  return {
    ...refactoredTicket,
    companyName: refactoredTicket.companyName || companyName,
    productName: refactoredTicket.productName || productName,
    companyWebsite: refactoredTicket.companyWebsite || payload.company_nf_website || '',
    productWebsite: refactoredTicket.productWebsite || payload.product_nf_website || '',
  }
}

async function updateTicket(ticketId, updateData) {
  const payload = {
    ticket_id: ticketId,
  }

  if (updateData.answers) {
    const answersToSend = omitFileUploadAnswers(updateData.answers)
    if (Object.keys(answersToSend).length > 0) {
      payload.section_to_update = 'questions_answers'
      payload.update_data = answersToSend
    }
  } else if (updateData.submitTicket) {
    payload.submit_ticket = true
  } else if (updateData.deleteTicket) {
    payload.delete_ticket = true
  }

  // Nothing to update (e.g. answers map was only file_upload ids)
  if (!payload.section_to_update && !payload.submit_ticket && !payload.delete_ticket) {
    return null
  }

  const res = await httpService.post(TICKET_ENDPOINTS.UPDATE_TICKET, payload)

  // Handle different response formats:
  // - Sometimes wrapped in res.ticket
  // - Sometimes directly in res
  const ticketData = res.ticket || res

  const refactoredTicket = refactorTicket({
    ticket_info: {
      ticket_id: ticketId,
      ...ticketData,
    },
  })

  return refactoredTicket
}

async function uploadFileToTicket({ ticketId, file, questionId }) {
  try {
    const formData = new FormData()
    formData.append('ticket_id', ticketId)
    formData.append('file', file)
    if (typeof questionId === 'string' && questionId !== '') {
      formData.append('question_id', questionId)
    }
    const res = await httpService.post(TICKET_ENDPOINTS.FILE_UPLOAD, formData)

    // Validate that response data exists and is not empty
    if (isObjectEmpty(res.uploaded_files)) {
      throw new Error('No files were uploaded')
    }

    // Refactor uploaded file to camelCase (single file upload)
    const uploadedFile = refactorUploadedFiles(res.uploaded_files)

    log.info('File uploaded successfully', { ticketId, uploadedFile })
    return uploadedFile
  } catch (error) {
    log.error('Failed to upload file', error)
    throw error
  }
}

async function deleteFileFromTicket(ticketId, fileId) {
  try {
    await httpService.post(TICKET_ENDPOINTS.FILE_DELETE, {
      ticket_id: ticketId,
      file_id: fileId,
    })

    log.info('File deleted successfully', { ticketId, fileId })
    return fileId
  } catch (error) {
    log.error('Failed to delete file', error)
    throw error
  }
}

async function getFileLink(ticketId, fileId) {
  try {
    const res = await httpService.post(TICKET_ENDPOINTS.FILE_GET_LINK, {
      ticket_id: ticketId,
      file_id: fileId,
    })

    // Validate that response data exists
    if (!res.url) {
      throw new Error('No file link found')
    }

    log.info('File link retrieved successfully', { ticketId, fileId })
    return {
      url: res.url,
      linkExpires: res.link_expires,
    }
  } catch (error) {
    log.error('Failed to get file link', error)
    throw error
  }
}
